import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Phone,
  Clock,
  FileText,
  MessageSquareText,
  User,
  Stethoscope,
  CalendarCheck,
} from 'lucide-react';
import { fmtDuration } from '../lib/format';
import { CallRecording } from './CallRecording';
import { CallTranscript } from './CallTranscript';
import { CallContactLink } from './CallContactLink';
import { specialistName } from '../lib/specialist-name';

export type CallRow = {
  id: string;
  conversationId: string | null;
  callerName: string | null;
  callerPhone: string | null;
  assistantId: string | null;
  startedAt: string | Date | null;
  endedAt: string | Date | null;
  endedReason: string | null;
  durationSec: number | null;
  cost: number | null;
  summary: string | null;
  // Transcript + raw messages now live in a 1:1 relation (fetched via
  // include: { transcript: true }); absent when not joined.
  transcript?: { text: string | null; messages?: unknown } | null;
  createdAt: string | Date;
  // Whether a booking was made during the call (+ the doctor it touched). Both
  // optional — only present when the query joins the specialist relation.
  booked?: boolean | null;
  specialist?: { user?: { name: string | null; email: string } | null } | null;
};

/** Full detail for one call — opened in the right split panel on row click.
 *  Shows summary, the audio recording (if any), and the raw transcript. */
export function CallDetailPanel({ call: c }: { call: CallRow }) {
  const { t, i18n } = useTranslation();

  const dtFmt = useMemo(
    () =>
      new Intl.DateTimeFormat(i18n.language, {
        weekday: 'short',
        day: 'numeric',
        month: 'short',
        hour: '2-digit',
        minute: '2-digit',
      }),
    [i18n.language],
  );

  const when = c.startedAt ?? c.createdAt;
  const doctorName = c.specialist ? specialistName(c.specialist as never) : null;

  return (
    <div className="flex h-full flex-col">
      <header className="shrink-0 border-b border-border px-4 py-3 flex items-center gap-2">
        <Phone className="h-4 w-4 shrink-0 text-primary" />
        <div className="min-w-0">
          <div className="text-sm font-medium text-foreground">
            {when ? dtFmt.format(new Date(when)) : t('calls.unknownDate')}
          </div>
          <div className="text-xs text-muted-foreground truncate">
            {c.endedReason || t('calls.detailSubtitle')}
          </div>
        </div>
      </header>

      <div className="flex-1 overflow-auto p-4 space-y-4">
        {/* Quick stats */}
        <div className="grid grid-cols-2 gap-2">
          <Stat
            icon={<Clock className="h-4 w-4" />}
            label={t('dashboard.cards.time')}
            value={fmtDuration(c.durationSec ?? 0)}
          />
        </div>

        {/* Caller */}
        {c.callerName || c.callerPhone ? (
          <section className="space-y-1">
            <SectionTitle icon={<User className="h-3.5 w-3.5" />}>
              {t('calls.caller')}
            </SectionTitle>
            <div className="text-sm leading-relaxed">
              {c.callerName ? <div className="font-medium">{c.callerName}</div> : null}
              {c.callerPhone ? (
                <a href={`tel:${c.callerPhone}`} className="text-primary tabular-nums">
                  {c.callerPhone}
                </a>
              ) : null}
            </div>
          </section>
        ) : null}

        {/* CRM contact — link/detach the call to a patient contact. */}
        <CallContactLink callId={c.id} />

        {/* Booking outcome — which doctor + whether a reservation was made. */}
        <section className="space-y-1">
          <SectionTitle icon={<CalendarCheck className="h-3.5 w-3.5" />}>
            {t('calls.booking')}
          </SectionTitle>
          <div className="flex flex-wrap items-center gap-2 text-sm">
            {c.booked ? (
              <span className="inline-flex items-center gap-1 rounded bg-primary/10 px-1.5 py-0.5 text-[11px] font-medium text-primary">
                <CalendarCheck className="h-3 w-3 shrink-0" />
                {t('calls.bookedYes')}
              </span>
            ) : (
              <span className="text-muted-foreground">{t('calls.bookedNo')}</span>
            )}
            {doctorName ? (
              <span className="inline-flex items-center gap-1.5">
                <Stethoscope className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
                <span>{doctorName}</span>
              </span>
            ) : null}
          </div>
        </section>

        {/* Recording — streamed on demand from the ElevenLabs proxy. */}
        <CallRecording conversationId={c.conversationId} />

        {/* Summary */}
        {c.summary ? (
          <section className="space-y-1">
            <SectionTitle icon={<FileText className="h-3.5 w-3.5" />}>
              {t('calls.summary')}
            </SectionTitle>
            <p className="text-sm leading-relaxed">{c.summary}</p>
          </section>
        ) : null}

        {/* Transcript */}
        <section className="space-y-1">
          <SectionTitle icon={<MessageSquareText className="h-3.5 w-3.5" />}>
            {t('calls.transcript')}
          </SectionTitle>
          <CallTranscript messages={c.transcript?.messages} transcript={c.transcript?.text ?? null} callerName={c.callerName} />
        </section>

        {/* Identifiers */}
        <div className="pt-2 border-t border-border text-[11px] text-muted-foreground space-y-0.5">
          {c.conversationId ? <div className="truncate">Conversation: {c.conversationId}</div> : null}
          {c.assistantId ? <div className="truncate">Assistant: {c.assistantId}</div> : null}
        </div>
      </div>
    </div>
  );
}

function Stat({
  icon,
  label,
  value,
}: {
  icon: React.ReactNode;
  label: string;
  value: string;
}) {
  return (
    <div className="rounded-md border border-border bg-card p-2">
      <div className="flex items-center gap-1.5 text-muted-foreground">
        {icon}
        <span className="text-[10px] font-medium">{label}</span>
      </div>
      <div className="text-sm font-semibold tabular-nums">{value}</div>
    </div>
  );
}

function SectionTitle({
  icon,
  children,
}: {
  icon?: React.ReactNode;
  children: React.ReactNode;
}) {
  return (
    <div className="flex items-center gap-1.5 text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
      {icon}
      <span>{children}</span>
    </div>
  );
}
